import { flushSync } from 'react-dom'
import { gsap } from 'gsap'

let switchingLayouts = false

/** Runs a layout switch inside a view transition, so the shared card morphs between panel and full screen. */
export const withViewTransition = (update: () => void) => {
  if (!document.startViewTransition) {
    update()
    return
  }
  switchingLayouts = true
  void document
    .startViewTransition(() => flushSync(update))
    .finished.finally(() => {
      switchingLayouts = false
    })
}

/** Slide the panel in from the right on mount, unless a view transition is already morphing it in from full screen. */
export const slideIn = (el: HTMLElement) => {
  if (switchingLayouts) return
  gsap.from(el, { x: '100%', duration: 0.4, ease: 'power3.out' })
}

/** Slide the panel out to the right on close, then call onComplete. */
export const slideOut = (el: HTMLElement, onComplete?: () => void) => {
  gsap.to(el, { x: '100%', duration: 0.3, ease: 'power3.in', onComplete })
}

interface CompactRefs {
  headerWrapper: HTMLElement | null
  contentSection: HTMLElement | null
  buttonsRow: HTMLElement | null
  closeButton: HTMLElement | null
  inputArea: HTMLElement | null
}

/**
 * Animate the panel header between expanded (compact=false) and compact
 * (compact=true) states. Collapses the ghost-buttons row, shrinks header
 * padding, and fades the standalone close button.
 */
export const animateCompact = (refs: CompactRefs, compact: boolean) => {
  const { headerWrapper, contentSection, buttonsRow, closeButton, inputArea } =
    refs
  if (!headerWrapper || !contentSection || !buttonsRow || !closeButton) return

  gsap.to(headerWrapper, {
    paddingTop: compact ? 12 : 24,
    duration: 0.3,
    ease: 'power2.inOut',
  })
  gsap.to(contentSection, {
    marginTop: compact ? 12 : 16,
    duration: 0.3,
    ease: 'power2.inOut',
  })
  gsap.to(buttonsRow, {
    autoAlpha: compact ? 0 : 1,
    height: compact ? 0 : 40,
    duration: 0.25,
    ease: 'power2.inOut',
  })
  gsap.to(contentSection, {
    rowGap: compact ? 0 : 12,
    duration: 0.25,
    ease: 'power2.inOut',
  })
  gsap.to(closeButton, {
    autoAlpha: compact ? 0 : 1,
    duration: 0.2,
    ease: 'power2.inOut',
  })
  if (inputArea) {
    gsap.to(inputArea, {
      marginTop: compact ? 20 : 8,
      duration: 0.25,
      ease: 'power2.inOut',
    })
  }
}

/**
 * Crossfade the ChatPanelHeader between its expanded and compact states.
 * Fades the expanded row out/up and the compact row in (or vice versa).
 * Returns a cleanup function that kills the in-flight tweens.
 */
export const animateHeaderCompact = (
  expandedEl: HTMLElement | null,
  compactEl: HTMLElement | null,
  compact: boolean
): (() => void) => {
  const targets = [expandedEl, compactEl]
  gsap.killTweensOf(targets)

  if (compact) {
    gsap.to(expandedEl, {
      autoAlpha: 0,
      y: -6,
      duration: 0.22,
      ease: 'power2.in',
      overwrite: 'auto',
    })
    gsap.to(compactEl, {
      autoAlpha: 1,
      y: 0,
      duration: 0.28,
      ease: 'power2.out',
      delay: 0.1,
      overwrite: 'auto',
    })
  } else {
    gsap.to(compactEl, {
      autoAlpha: 0,
      y: -6,
      duration: 0.2,
      ease: 'power2.in',
      overwrite: 'auto',
    })
    gsap.to(expandedEl, {
      autoAlpha: 1,
      y: 0,
      duration: 0.28,
      ease: 'power2.out',
      delay: 0.08,
      overwrite: 'auto',
    })
  }

  return () => gsap.killTweensOf(targets)
}

/**
 * Animated "new conversation" transition: clones the current inner content,
 * calls onCleared (which triggers React to re-render with empty content),
 * then fades out the clone and slides the new content up.
 */
export const animateNewConversation = (
  innerEl: HTMLElement,
  cardEl: HTMLElement,
  onCleared: () => void
) => {
  const node = innerEl.cloneNode(true)
  if (!(node instanceof HTMLElement)) {
    onCleared()
    return
  }
  const clone = node
  clone.style.cssText =
    'position:absolute;inset:0;z-index:10;pointer-events:none;'
  cardEl.appendChild(clone)

  // Clear messages — React re-renders innerEl with new (empty) content
  onCleared()

  // Fade out old content clone; slide new content up from below
  gsap.to(clone, {
    autoAlpha: 0,
    duration: 0.3,
    ease: 'power2.in',
    onComplete: () => clone.remove(),
  })
  gsap.from(innerEl, {
    y: 50,
    autoAlpha: 0,
    duration: 0.45,
    ease: 'power3.out',
    delay: 0.05,
  })
}
